import type { OAuthPrefill, OAuthProvider } from '../types';

/**
 * Shape of the `/auth/google` and `/auth/facebook` responses. Both fields
 * and both "user states" live behind HTTP 200, distinguished at runtime by
 * which keys are present:
 *
 *   - Existing user → `{ token, email }`
 *   - New user      → `{ uid, email, fullname }`
 *
 * Facebook can also return `{ uid, fullname }` without `email` when the
 * user's Facebook account is email-less or their privacy settings hide it.
 */
export interface OAuthBackendResponse {
  token?: string;
  email?: string;
  userId?: string;
  uid?: string;
  fullname?: string;
}

/**
 * Tagged union describing what the caller should do with the response.
 * Separated from the hook so the branching logic is testable without
 * rendering React or mocking the Google/Facebook SDKs.
 *
 * - `success` → backend returned a JWT. Caller logs the user in.
 * - `needs-signup` → backend returned prefill (`uid + email`). Caller
 *   navigates to the prefilled signup form.
 * - `facebook-missing-email` → backend returned prefill but no email.
 *   Caller cannot proceed (signup needs email) and shows a provider-
 *   specific error. Only fires when `provider === 'facebook'`.
 * - `error` → response is malformed (no token, no usable prefill). Caller
 *   shows a generic error and captures diagnostic Sentry data.
 */
export type OAuthRouteAction =
  | { kind: 'success'; token: string; email: string }
  | { kind: 'needs-signup'; prefill: OAuthPrefill }
  | { kind: 'facebook-missing-email'; hasFullname: boolean }
  | { kind: 'error'; reason: 'broken-response'; responseKeys: string[]; hasEmail: boolean; hasUserId: boolean; hasUid: boolean };

/**
 * Decide what to do with an OAuth backend response. Pure function — no
 * React, no Sentry, no SDK. The hook layers side effects on top of the
 * returned action.
 */
export function routeOAuthResponse(
  provider: OAuthProvider,
  result: OAuthBackendResponse | null | undefined,
): OAuthRouteAction {
  if (result?.token) {
    return { kind: 'success', token: result.token, email: result.email ?? '' };
  }

  if (result?.email && result?.uid) {
    return {
      kind: 'needs-signup',
      prefill: {
        provider,
        email: result.email,
        fullname: result.fullname ?? '',
        uid: result.uid,
      },
    };
  }

  // Facebook allows email-less accounts. If we have a uid but no email, we
  // cannot register the user (email is the backend identity key), so the
  // caller should show a Facebook-specific error. For Google this branch
  // never triggers because Google always includes email in the profile
  // response when the `email` scope is granted (which we request).
  if (provider === 'facebook' && result?.uid && !result?.email) {
    return { kind: 'facebook-missing-email', hasFullname: !!result?.fullname };
  }

  return {
    kind: 'error',
    reason: 'broken-response',
    responseKeys: result ? Object.keys(result).sort() : [],
    hasEmail: !!result?.email,
    hasUserId: !!result?.userId,
    hasUid: !!result?.uid,
  };
}
